<template>
  <section>
    <!-- Progress Bar(s) -->
    <div class="bg-grey-background-subdued sm:bg-white p-16 sm:box-shadow-sm">
      <p v-if="progressBarTitle" class="text-overline text-grey-header">{{ progressBarLabel }}</p>
      <slot name="progressBar" :currentStep="currentStep" :maxSteps="steps.length + 1" />
    </div>
    <div class="fixed-height flex justify-center">
      <div class="sm:mt-16 fixed-height" :class="cardWidthClass">
        <!-- Breadcrumb + Header -->
        <div class="w-full pl-16 sm:pl-0">
          <Breadcrumb
            v-if="!isFirstStep"
            :aria-label="`Back to ${breadcrumbHeader}`"
            class="mb-16"
            :header="breadcrumbHeader"
            :isButton="true"
            @click="previousStep"
          />
          <h1 class="text-h2 mr-12">{{ step.title }}</h1>
          <p v-if="step.required" class="text-small text-grey-text-subdued mt-4">Required</p>
        </div>
        <!-- Stepper Content -->
        <Card class="form-stepper-card sm:mt-16 box-shadow-none sm:box-shadow-sm sm:p-24">
          <component
            :is="step.component"
            v-if="step.component"
            :key="currentStep"
            ref="stepComponent"
            class="step-component"
            :class="step.xlCard && 'xl'"
          />
          <div v-else>{{ step.title }} content</div>
          <div class="sm:mt-24" :class="stickyButtonWidthClass">
            <StickyButton
              :value="buttonValue"
              :loading="loading || isAdvancing"
              :pageHasBottomNav="pageHasBottomNav"
              @click="handleAction"
            />
          </div>
        </Card>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import Breadcrumb from '../breadcrumb/Breadcrumb.vue';
import Card from '../card/Card.vue';
import StickyButton from '../sticky_button/StickyButton.vue';
import { markComponentRaw } from '@/helpers/markComponentRaw';

interface Step {
  name: string;
  title: string;
  required: boolean;
  component: any;
  alwaysFullWidthButton: boolean;
  xlCard: boolean;
  nextStepCallback: () => void;
  previousStepCallback: () => void;
}

export default {
  name: 'FormStepper',
  components: { Breadcrumb, Card, StickyButton },
  emits: ['submit'],
  props: {
    progressBarTitle: {
      type: String,
      default: '',
    },
    progressBarLastStepTitle: {
      type: String,
      default: '',
    },
    steps: {
      type: Array<Step>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pageHasBottomNav: {
      type: Boolean,
      default: false,
    },
    lastStepButtonText: {
      type: String,
      default: 'Submit',
    },
  },
  data() {
    return {
      currentStep: 1,
      isAdvancing: false,
    };
  },
  computed: {
    step(): Step {
      const stepData = this.steps[this.currentStep - 1] ?? this.steps[0];
      return stepData.component ? { ...stepData, component: markComponentRaw(stepData.component) } : stepData;
    },
    cardWidthClass(): String {
      return this.step.xlCard ? 'max-w-688' : 'max-w-488';
    },
    stickyButtonWidthClass(): String {
      return this.step.alwaysFullWidthButton ? '' : 'lg:max-w-max';
    },
    progressBarLabel(): string {
      return this.isLastStep ? this.progressBarLastStepTitle : this.progressBarTitle;
    },
    breadcrumbHeader(): string {
      return this.steps[this.currentStep - 2]?.name;
    },
    isFirstStep(): boolean {
      return this.currentStep === 1;
    },
    isLastStep(): boolean {
      return this.currentStep === this.steps.length;
    },
    buttonValue(): string {
      if (this.isLastStep) {
        return this.lastStepButtonText;
      }
      return 'Next';
    },
  },
  watch: {
    currentStep() {
      this.scrollToTop();
    },
  },
  methods: {
    previousStep() {
      if (this.isAdvancing || this.loading || this.currentStep <= 1) {
        return;
      }

      this.step.previousStepCallback();
      this.currentStep -= 1;
    },
    nextStep() {
      if (this.currentStep >= this.steps.length) {
        return;
      }

      this.currentStep += 1;
      this.focusAfterStepAdvance();
    },
    validateStep() {
      const component = this.$refs.stepComponent as { validate?: () => unknown };
      return !component?.validate || component.validate() === true;
    },
    focusStepContent(options?: { scroll?: boolean }) {
      this.$nextTick(() => {
        const component = this.$refs.stepComponent as { focusElement?: (options?: { scroll?: boolean }) => void };
        component?.focusElement?.(options);
      });
    },
    focusAfterStepAdvance(onComplete?: () => void) {
      this.$nextTick(() => {
        const component = this.$refs.stepComponent as { focusElement?: (options?: { scroll?: boolean }) => void };
        component?.focusElement?.({ scroll: false });
        this.scrollToTop();
        onComplete?.();
      });
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    },
    handleAction() {
      if (this.isAdvancing || this.loading) {
        return;
      }

      this.isAdvancing = true;

      if (!this.validateStep()) {
        this.isAdvancing = false;
        this.focusStepContent();
        return;
      }

      this.step.nextStepCallback();

      if (this.isLastStep) {
        this.isAdvancing = false;
        this.$emit('submit');
        return;
      }

      this.currentStep += 1;
      this.focusAfterStepAdvance(() => {
        this.isAdvancing = false;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.form-stepper-card {
  @media (max-width: 480px) {
    background-color: #fbfbfb !important;
  }
}

// Preserve height so content does not jump (especially mobile sticky button, footer)
.fixed-height {
  min-height: calc(100vh - 118px);

  @media only screen and (min-width: 480px) {
    height: auto;
    min-height: calc(100vh - 118px);
  }

  // main class margin change breakpoint
  @media only screen and (min-width: 768px) {
    min-height: calc(100vh - 150px);
  }
}

// Preserve width so that flexbox content does not jump (e.g. toggle token group components)
.step-component {
  width: calc(100vw - 32px);
  @media only screen and (min-width: 480px) {
    &:not(.xl) {
      width: 432px;
    }
    &.xl {
      width: calc(100vw - 72px);
    }
  }

  @media only screen and (min-width: 688px) {
    &.xl {
      width: 620px;
    }
  }
}
</style>
